import React, { useRef } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import Paginator from "./Paginator";
import ColumnFilter from "./ColumnFilter";
import { Checkbox } from "./Choice";
import useColumnResize from "./useColumnResize";
import useScrollEdges from "./useScrollEdges";
import cn from "./cn";

/**
 * AppDataTable — the app's table.
 *
 * ── Data mode (prefer this) ──────────────────────────────────────────────────
 * Pass `rows` and let the columns render the cells:
 *
 *   <AppDataTable
 *     columns={[
 *       { key: 'name',   label: 'Name',   sortable: true },
 *       { key: 'status', label: 'Status', align: 'center',
 *         render: (row) => <StatusBadge status={row.status} /> },
 *     ]}
 *     rows={students}
 *     rowKey={(s) => s._id}
 *     onRowClick={(s) => navigate(`/students/${s._id}`)}
 *     loading={loading}
 *     pagination={{ page, totalPages, total, pageSize, onChange }}
 *   />
 *
 * The component owns the `<td>`s here, so padding and alignment are the same on
 * every screen, and the loading shimmer, sortable headers and keyboard-reachable
 * clickable rows come for free.
 *
 * ── Children mode ────────────────────────────────────────────────────────────
 * Pass `<tr>` elements as children and the call site owns the cells. Reach for
 * it only when a row is genuinely unusual — a colspan summary, a nested
 * expander. Cells are then styled by the descendant rules below, because this
 * component never sees that markup.
 *
 * ── Layout ───────────────────────────────────────────────────────────────────
 * `fill` (default true) makes the card fill its flex parent with a sticky header
 * and sticky paginator, so only the body scrolls — put it inside a flex-column
 * page. `fill={false}` lets the table flow with the page instead, which is what
 * a table inside a dashboard card wants.
 *
 * `toolbar` is a bar INSIDE the card, above the table and below nothing —
 * usually a `FilterToolbar` carrying the search, the filters and the page
 * action. It is the mirror of the `pagination` footer, and it exists because a
 * strip of controls floating above a bordered card does not read as belonging
 * to the table it filters. It sits OUTSIDE the scroll container, so the sticky
 * `<thead>` still docks under it rather than scrolling past it.
 *
 * ⚠️ A `FilterToolbar` in this slot wants `flush` — its own bottom margin is
 * spacing between it and a separate table, and here the bar's padding is the
 * spacing.
 *
 * ── Column filters (ag-Grid style) ──────────────────────────────────────────
 * A column declaring `filter` gets a funnel beside its label, so sorting and
 * filtering live together in the header cell rather than in a strip that has to
 * name the columns again:
 *
 *   { key: 'status', label: 'Status', sortable: true,
 *     filter: { type: 'set', options: STATUS_OPTIONS },
 *     filterValue: (row) => row.status }
 *
 * `columnFilters` / `onColumnFilterChange` are CONTROLLED — the page owns the
 * values, exactly as it owns `sort`. That is what lets a server-side table send
 * the same values upstream instead of filtering in the browser; a client-side
 * one calls `applyColumnFilters(rows, columns, columnFilters)`, which is
 * exported from this file so no page writes its own matching.
 *
 * `filterValue` is the accessor for BOTH the filter and, where the page uses it,
 * the sort. It defaults to `row[key]`, which is rarely enough — most real
 * columns read through a nested field or format one.
 *
 * ── Pinned columns ──────────────────────────────────────────────────────────
 * `pinned: 'right' | 'left'` freezes a column against that edge of the scroll
 * container, so an action menu stays reachable on a table wider than the
 * viewport. One per side — see `PINNED_BODY_CLASSES` for why.
 *
 * ── Row selection ───────────────────────────────────────────────────────────
 * `selectable` prepends a checkbox column and reports ids through
 * `onSelectionChange`. The page owns the array, as it owns `sort`:
 *
 *   <AppDataTable selectable selectedRows={ids} onSelectionChange={setIds}
 *                 rowKey={(s) => s._id} … />
 *
 * ⚠️ An id comes from `rowKey` — the SAME function React's key uses, so the box
 * cannot tick one row and record another.
 *
 * ⚠️ Select-all covers THIS PAGE only, adding to and removing from whatever is
 * already selected. On a server-paginated list the selection outlives the page,
 * and replacing the array would silently drop page 1's ticks on reaching page 2.
 *
 * The checkbox column pins LEFT, which costs nothing: an actions column pins
 * right, and one per side is exactly what is supported.
 *
 * ── Other row hooks ─────────────────────────────────────────────────────────
 *   rowClassName(row, i)       — extra classes per row (a muted inactive row)
 *   onRowContextMenu(row, e)   — right-click, for a page with its own menu
 *   renderExpandedRow(row, i)  — a full-width `<tr>` under the row when truthy
 *
 * ── Nothing to show ─────────────────────────────────────────────────────────
 * `emptyMessage` is a string; `emptyState` is a node, for the case where the
 * answer is not "there is nothing" but "there is nothing THAT MATCHES" and the
 * user needs a way out:
 *
 *   <AppDataTable emptyState={<EmptyState title="No matches" action={clear} />} … />
 *
 * ⚠️ Render the empty state IN the table, never instead of it. A page that
 * swaps the whole component for an `EmptyState` when the list comes back empty
 * unmounts its own column filters and its own toolbar — so the filter that
 * emptied the list is the one control the user can no longer reach. That was
 * live on `/enquiries` until 2026-09-07.
 *
 * ⚠️ The empty row pins itself to the LEFT EDGE OF THE SCROLL PORT, at
 * `useScrollEdges`' measured `viewport` width. A `colSpan` cell is as wide as
 * the whole table, so `text-align: center` on a table wider than the screen
 * centres the message somewhere off to the right of it — the wider the table,
 * the further out of view. Sticky keeps it where the reader is.
 *
 * Column: { key, label, width?, align?, sortable?, render?, className?,
 *           headClassName?, filter?, filterValue?, pinned?, resizable?,
 *           minWidth? }
 */

/* The filtering and sorting these headers drive are pure, and live in
   `tableData.js` so they can be tested without a component tree. Re-exported
   here because every page imports them alongside this component. */
export { applyColumnFilters, sortRows } from "./tableData";

/* The card, plus the descendant rules for the `<tr>`/`<td>` markup children mode
   supplies — the one case this component cannot style at the element itself. */
const TABLE_CARD_CLASSES =
  // Same material as AppCard: 12px radius (`tw-rounded` is remapped), a 1px
  // line and the resting elevation step. A table card takes no padding — the
  // cells provide it — but it must not read as a different surface from the
  // cards above and below it on the page.
  "tw-flex tw-flex-col tw-bg-surface-raised tw-border tw-border-line tw-rounded " +
  "tw-shadow-card tw-overflow-hidden " +
  // table
  "[&_table]:tw-w-full [&_table]:tw-border-separate [&_table]:tw-border-spacing-0 " +
  // sticky head. The inset shadow keeps the bottom border visible while stuck —
  // a real border scrolls away with the cell box.
  "[&_thead_th]:tw-sticky [&_thead_th]:tw-top-0 [&_thead_th]:tw-z-[2] " +
  "[&_thead_th]:tw-bg-muted [&_thead_th]:tw-text-[0.6875rem] " +
  "[&_thead_th]:tw-font-semibold [&_thead_th]:tw-uppercase " +
  "[&_thead_th]:tw-tracking-caps [&_thead_th]:tw-text-content-subtle " +
  "[&_thead_th]:tw-px-5 [&_thead_th]:tw-py-3 [&_thead_th]:tw-text-left " +
  "[&_thead_th]:tw-border-b [&_thead_th]:tw-border-line-soft " +
  "[&_thead_th]:tw-shadow-[inset_0_-1px_0_var(--border-secondary)] " +
  // Column rules, header only. Without them a header cell's label runs into its
  // neighbour's controls and there is nothing to say which sort arrow or funnel
  // belongs to which column — the more so now that a header can hold two
  // controls. The body stays open: ruling every cell as well turns a table into
  // a spreadsheet, and the row is the thing being read there.
  //
  // ⚠️ A PSEUDO-ELEMENT, not `border-r`. A border is the height of its box, so a
  // border-drawn rule runs the full cell and reads as a grid — heavier than a
  // header needs, and it collides with the bottom rule at every corner. A 16px
  // tick centred on the cell separates the columns and stays quiet. There is no
  // border property that can be shorter than the element, which is the whole
  // reason this is `::after` and not one more `tw-border-*`.
  //
  // ⚠️ It has its OWN colour and must keep it. Every other line in this card is
  // `--border-secondary`, drawn on `--card-bg`; the header sits on `--muted`,
  // and on THAT ground the same token is #f1f3f8 on #f3f4f6 — two values out of
  // 255, a rule that renders and cannot be seen. It shipped that way once.
  // `--muted-foreground` at 25% is the header's own ink, so it has real contrast
  // against the header's own fill and flips with the theme for free.
  //
  // The `<th>` is `position: sticky`, which is a positioned ancestor, so the
  // tick needs no `relative` of its own.
  "[&_thead_th]:after:tw-content-[''] [&_thead_th]:after:tw-absolute " +
  "[&_thead_th]:after:tw-right-0 [&_thead_th]:after:tw-top-1/2 " +
  "[&_thead_th]:after:-tw-translate-y-1/2 " +
  "[&_thead_th]:after:tw-h-4 [&_thead_th]:after:tw-w-px " +
  "[&_thead_th]:after:tw-bg-muted-fg/25 " +
  "[&_thead_th:last-child]:after:tw-hidden " +
  // body cells
  "[&_tbody_td]:tw-text-[0.8125rem] [&_tbody_td]:tw-text-content " +
  "[&_tbody_td]:tw-px-5 [&_tbody_td]:tw-py-3.5 [&_tbody_td]:tw-align-middle " +
  "[&_tbody_td]:tw-border-b [&_tbody_td]:tw-border-line-soft " +
  "[&_tbody_tr:last-child_td]:tw-border-b-0";

const FILL_HEIGHT_CLASSES = "tw-flex-1 tw-min-h-0";

const COLUMN_ALIGN_CLASSES = {
  left: "tw-text-left",
  center: "tw-text-center",
  right: "tw-text-right",
};

/**
 * A frozen column — ag-Grid's `pinned`. The cell stops scrolling sideways and
 * parks against its edge of the scroll container, which is what stops the row
 * actions from being unreachable on a wide table: you would otherwise scroll
 * down to the row you want and then scroll right to act on it, losing the row.
 *
 * ⚠️ ONE pinned column per side. Two would each need `right: <width of the ones
 * outside it>`, and that width is only knowable after layout — the offset math
 * is the whole feature, not a detail, so it is deliberately not attempted here.
 * A second `pinned` column on the same side silently stacks on the first.
 *
 * ── Resizable columns ───────────────────────────────────────────────────────
 * `resizable` puts a drag handle on the right edge of every header cell, and
 * `storageKey` remembers where the user left them:
 *
 *   <AppDataTable resizable storageKey="sa-institutes" … />
 *
 * Opt a column out with `resizable: false` — an icon-only actions column has no
 * width worth choosing. `minWidth` (default 72) is the floor for the rest.
 * Double-click, or Enter on the focused handle, restores the width the browser
 * measured from the content; the arrow keys nudge it by 16px, or 64 with Shift.
 *
 * ⚠️ Resizing means `table-layout: fixed`, so a cell no longer widens its column
 * to fit — long text WRAPS instead. The mechanics, and why the table measures
 * itself before it will resize, are in `useColumnResize.js`.
 *
 * ⚠️ `!` on the colour, and it is required. `[&_tbody_td]:tw-border-line-soft`
 * sets `border-color` for every side at (0,1,2); a bare `tw-border-l-line` on
 * the cell itself is (0,1,0) and loses for `border-left-color` even though it is
 * the longhand. The line is `--border-primary`, one step stronger than the row
 * rules, because it is not separating two columns — it is the edge of the part
 * of the table that does not move.
 */
const PINNED_BODY_CLASSES = {
  // `bg-inherit`, not a colour of its own: the row carries the background, so
  // the frozen cell picks up the hover tint with it. A fixed `bg-surface-raised`
  // here would be a cell that stays pale while its own row highlights.
  right: "tw-sticky tw-right-0 tw-z-[1] tw-bg-inherit tw-border-l !tw-border-l-line",
  left: "tw-sticky tw-left-0 tw-z-[1] tw-bg-inherit tw-border-r !tw-border-r-line",
};

// The head cell is already `sticky` + `z-[2]` from the card rules, so it only
// needs the second axis and a z-index above the frozen BODY cells (z-1) that
// scroll up underneath it. Both are `!` for the same specificity reason.
const PINNED_HEAD_CLASSES = {
  right: "tw-right-0 !tw-z-[3] tw-border-l !tw-border-l-line",
  left: "tw-left-0 !tw-z-[3] tw-border-r !tw-border-r-line",
};

/**
 * "There is more table this way."
 *
 * A wide table gives no sign that it is wide. The scroll POSITION is solved by
 * `fill` — a bounded card puts the horizontal scrollbar under the last row
 * instead of at the bottom of a 20-row page — but a scrollbar at rest still
 * does not say which direction has more in it.
 *
 * Two renderings, one measured state (`useScrollEdges`):
 *
 *  · Where a column is FROZEN to that edge, the shading goes on the frozen
 *    cells. That is the edge the rows disappear under, and it is what every
 *    spreadsheet draws. A fade painted OVER a frozen column would say the
 *    frozen column scrolls, which is the one thing it does not do.
 *  · Where nothing is frozen there is nothing to cast a shadow, so a gradient
 *    sits over the container's own edge instead.
 *
 * The component already knows which case it is in (`col.pinned`), so this is
 * one decision made from one boolean, not two mechanisms.
 *
 * ⚠️ Arrow buttons were the other option and are deliberately NOT here. Once
 * the card is bounded the scrollbar is visible directly under the rows, so
 * arrows would be permanent chrome on all 26 tables duplicating a control the
 * user can already see — and they would have to be positioned over a sticky
 * header or a frozen column. `useScrollEdges` returns exactly what arrows would
 * need if that turns out to be wrong.
 */
const EDGE_FADE_CLASSES =
  // ⚠️ A SIBLING of the scroll container, never a child — a child scrolls away
  // with the rows on the first swipe. `inset-y-0` is the wrapper's, so it never
  // covers the toolbar or the paginator.
  "tw-pointer-events-none tw-absolute tw-inset-y-0 tw-z-[3] tw-w-6 " +
  "tw-to-transparent tw-transition-opacity tw-duration-150 " +
  // Reduced motion removes the FADE, not the shading: the hint is the point.
  "motion-reduce:tw-transition-none";

/**
 * Cast by a frozen column over the rows sliding under it. Offset and spread are
 * equal and opposite so it reads as a soft edge rather than a box, and it goes
 * on the `<th>` as well so the shadow runs the table's full height.
 *
 * `--card-shadow-hover` rather than a literal: it is themed, and it is already
 * `transparent` under the print token swap, so this costs nothing on paper.
 */
const PINNED_EDGE_CLASSES = {
  right: "tw-shadow-[-6px_0_6px_-6px_var(--card-shadow-hover)]",
  left: "tw-shadow-[6px_0_6px_-6px_var(--card-shadow-hover)]",
};

/**
 * The drag handle, on the right edge of a header cell.
 *
 * ⚠️ It stays INSIDE its own cell (`right-0`, no overhang). A handle straddling
 * the boundary is a larger target and it does not work here: every `<th>` is
 * `position: sticky` with an opaque background and a z-index of its own, so the
 * next header cell — later in the DOM, painted on top — would swallow both the
 * pixels and the pointer events of anything hanging over the line.
 *
 * The line is drawn by a pseudo-element rather than a border, so the 10px grab
 * zone can be wider than the 2px mark, and it is transparent until hovered,
 * focused or dragged — the header already has its own column ticks, and a
 * second permanent rule beside each one reads as a rendering fault.
 */
const RESIZER_CLASSES =
  "tw-absolute tw-top-0 tw-right-0 tw-z-[1] tw-h-full tw-w-2.5 tw-cursor-col-resize " +
  "tw-touch-none tw-select-none tw-outline-none tw-focus-ring " +
  "after:tw-content-[''] after:tw-absolute after:tw-inset-y-1 after:tw-right-0 " +
  "after:tw-w-0.5 after:tw-rounded-full after:tw-bg-transparent " +
  "hover:after:tw-bg-primary focus-visible:after:tw-bg-primary";

/** A sortable column header: a real `<button>` inside the `<th>`, with
 *  `aria-sort` on the `<th>`. A `<th>` carrying an onClick is not reachable by
 *  keyboard and announces as a plain header. */
const SortableHeader = ({ column, sort, onSortChange }) => {
  const active = sort?.key === column.key;
  const direction = active ? sort.direction : null;
  const Icon = !active ? ChevronsUpDown : direction === "asc" ? ArrowUp : ArrowDown;

  return (
    <button
      type="button"
      onClick={() => onSortChange(column.key, active && direction === "asc" ? "desc" : "asc")}
      className={cn(
        "tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0",
        // `font` and `letter-spacing` are arbitrary PROPERTIES, which take no
        // tw- prefix — the button inherits the <th>'s type rather than restating
        // it, so a change to the header spec above reaches here for free.
        "[font:inherit] [letter-spacing:inherit] tw-text-inherit tw-cursor-pointer",
        "hover:tw-text-content",
        "tw-focus-ring"
      )}
    >
      {column.label}
      <Icon size={12} className={cn(!active && "tw-opacity-50")} aria-hidden="true" />
    </button>
  );
};

/** Placeholder rows shaped like the real ones. The app's loading convention is a
 *  shimmer that mirrors the layout, never a spinner over an empty box. */
const ShimmerRows = ({ columns, rows = 6 }) =>
  Array.from({ length: rows }).map((_, r) => (
    // The same background and frozen cells as a real row: a loading table that
    // scrolls its placeholders under a transparent action column is a different
    // table from the one that arrives a moment later.
    <tr key={`shimmer-${r}`} aria-hidden="true" className="tw-bg-surface-raised">
      {columns.map((col) => (
        <td key={col.key} className={cn(col.pinned && PINNED_BODY_CLASSES[col.pinned])}>
          <span className="tw-block tw-h-3.5 tw-w-full tw-max-w-[180px] tw-rounded-[3px] tw-bg-shimmer tw-animate-pulse-soft" />
        </td>
      ))}
    </tr>
  ));

const AppDataTable = ({
  columns = [],
  rows,
  rowKey,
  onRowClick,
  onRowContextMenu,
  rowClassName,
  renderExpandedRow,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  sort,
  onSortChange,
  loading = false,
  fill = true,
  toolbar,
  columnFilters = {},
  onColumnFilterChange,
  resizable = false,
  storageKey,
  pagination,
  isEmpty,
  emptyMessage = "No records found",
  emptyState,
  className = "",
  tableClassName = "",
  children,
}) => {
  const dataMode = Array.isArray(rows);
  const empty = isEmpty ?? (dataMode ? rows.length === 0 : false);

  /* ⚠️ ONE definition of a row's identity, used by React's `key` AND by
     selection. Two would drift the first time a page passed `rowKey` — the
     checkbox would tick a row and the list would hold a different id. */
  const idOf = (row, index) =>
    rowKey ? rowKey(row, index) : row?._id ?? row?.id ?? index;

  const selectedSet = new Set(selectedRows);
  const allIds = dataMode ? rows.map(idOf) : [];
  const allChecked = allIds.length > 0 && allIds.every((id) => selectedSet.has(id));
  const someChecked = allIds.some((id) => selectedSet.has(id));

  const toggleAll = () => {
    if (!onSelectionChange) return;
    // ⚠️ Only THIS page's ids are added or removed. On a server-paginated table
    // the selection outlives the page, so replacing the array with `allIds`
    // would silently drop everything ticked on page 1 the moment you reach
    // page 2 and press select-all.
    if (allChecked) onSelectionChange(selectedRows.filter((id) => !allIds.includes(id)));
    else onSelectionChange([...new Set([...selectedRows, ...allIds])]);
  };

  const toggleOne = (id) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      selectedSet.has(id) ? selectedRows.filter((x) => x !== id) : [...selectedRows, id]
    );
  };

  /* The select column is a REAL column, prepended — not a `<td>` smuggled into
     the row markup. `colgroup`, `colCount`, the resize measurement and the
     pinning offsets all walk `columns`, so a cell they cannot see puts every
     one of them out by one.

     It pins LEFT, which is free: the actions column pins right, and
     `PINNED_BODY_CLASSES` supports exactly one per side. */
  const selectColumn = {
    key: "__select",
    width: 44,
    resizable: false,
    pinned: "left",
    className: "!tw-py-0",
    headClassName: "!tw-py-0",
    label: (
      <Checkbox
        aria-label={allChecked ? "Clear selection" : "Select all rows on this page"}
        checked={allChecked}
        indeterminate={!allChecked && someChecked}
        onChange={toggleAll}
      />
    ),
    render: (row, index) => {
      const id = idOf(row, index);
      return (
        // The row may be clickable; ticking a box must not also navigate.
        <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <Checkbox
            aria-label="Select row"
            checked={selectedSet.has(id)}
            onChange={() => toggleOne(id)}
          />
        </span>
      );
    },
  };

  const effectiveColumns = selectable ? [selectColumn, ...columns] : columns;
  const colCount = effectiveColumns.length || 1;

  const scrollRef = useRef(null);
  const edges = useScrollEdges(scrollRef);
  const hasPinnedLeft = effectiveColumns.some((c) => c.pinned === "left");
  const hasPinnedRight = effectiveColumns.some((c) => c.pinned === "right");

  const resize = useColumnResize({
    columns: effectiveColumns,
    // Children mode has no `columns[].render`, but it still declares the header
    // cells — which is all a width applies to. What it must NOT do is resize
    // before there is something to measure.
    enabled: resizable && effectiveColumns.length > 0,
    loading,
    storageKey,
    scrollRef,
  });

  const renderCell = (row, col, index) =>
    col.render ? col.render(row, index) : row?.[col.key];

  const body = () => {
    if (loading) return <ShimmerRows columns={effectiveColumns.length ? effectiveColumns : [{ key: "x" }]} />;
    if (empty) {
      return (
        <tr>
          {/* ⚠️ The `!` is required: the descendant cell rules above are more
              specific than a bare utility, so the cell's own padding would
              otherwise survive and offset the pinned box inside it. */}
          <td colSpan={colCount} className="!tw-p-0">
            {/* Pinned to the scroll port rather than centred in the cell — see
                the note in the docblock. The width falls back to the cell's own
                while `viewport` is still unmeasured on the first paint. */}
            <div
              className="tw-sticky tw-left-0"
              style={{ width: edges.viewport ? `${edges.viewport}px` : "100%" }}
            >
              {emptyState || (
                <div className="tw-px-4 tw-py-12 tw-text-center tw-text-content-subtle tw-text-[0.8125rem]">
                  {emptyMessage}
                </div>
              )}
            </div>
          </td>
        </tr>
      );
    }
    if (!dataMode) return children;

    return rows.map((row, index) => {
      const clickable = Boolean(onRowClick);
      const id = idOf(row, index);
      const expanded = renderExpandedRow ? renderExpandedRow(row, index) : null;
      const tr = (
        <tr
          key={id}
          onClick={clickable ? () => onRowClick(row, index) : undefined}
          onContextMenu={onRowContextMenu ? (e) => onRowContextMenu(row, e) : undefined}
          // A clickable row must also be reachable and activatable by keyboard.
          // The alternative every hand-rolled table chose — onClick on a bare
          // <tr> — is a control no keyboard user can reach.
          tabIndex={clickable ? 0 : undefined}
          role={clickable ? "button" : undefined}
          onKeyDown={
            clickable
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(row, index);
                  }
                }
              : undefined
          }
          className={cn(
            // An explicit background, so a pinned cell's `bg-inherit` has
            // something opaque to take and the row's own content cannot show
            // through it while scrolling sideways.
            "tw-bg-surface-raised",
            clickable &&
              // The ring is INSET: an outline drawn outside a <tr> is clipped by
              // the card's overflow-hidden and the user sees no focus at all.
              "tw-cursor-pointer tw-transition-[background-color] hover:tw-bg-surface-hover tw-focus-ring-inset",
            // A selected row reads as selected even after the checkbox scrolls
            // out of view sideways. The tint is mixed onto the row's surface
            // rather than laid over it at 6 % alpha: a pinned cell inherits this
            // colour, and a translucent one let the cells scrolling under it show.
            selectable && selectedSet.has(id) && "!tw-bg-[color-mix(in_srgb,hsl(var(--primary))_6%,var(--card-bg))]",
            rowClassName && rowClassName(row, index)
          )}
        >
          {effectiveColumns.map((col) => (
            <td
              key={col.key}
              className={cn(
                COLUMN_ALIGN_CLASSES[col.align],
                col.pinned && PINNED_BODY_CLASSES[col.pinned],
                // The frozen column casts a shadow only while there is
                // something scrolled under it — see PINNED_EDGE_CLASSES.
                col.pinned && edges[col.pinned] && PINNED_EDGE_CLASSES[col.pinned],
                col.className
              )}
            >
              {renderCell(row, col, index)}
            </td>
          ))}
        </tr>
      );

      if (!expanded) return tr;
      /* An expanded row is a SIBLING `<tr>`, not a child of the row above —
         a `<tr>` may not contain another. The fragment keeps both under one
         key so React does not re-mount the pair on every render.
         ⚠️ `renderExpandedRow` returns CONTENT, not a `<tr>`. This component
         owns the row and the cell here exactly as it does everywhere else, so
         a call site cannot return a `<div>` and break the table's structure. */
      return (
        <React.Fragment key={id}>
          {tr}
          <tr className="tw-bg-surface-alt">
            <td colSpan={colCount} className="!tw-px-0 !tw-py-0">{expanded}</td>
          </tr>
        </React.Fragment>
      );
    });
  };

  return (
    <div className={cn(TABLE_CARD_CLASSES, fill && FILL_HEIGHT_CLASSES, className)}>
      {toolbar && (
        <div className="tw-shrink-0 tw-border-b tw-border-line-soft tw-px-4 tw-py-2.5 tw-bg-surface-raised">
          {toolbar}
        </div>
      )}

      {/* The scroll region, plus the shading over its edges. The shades are
          SIBLINGS of the scroll container — a child would scroll away with the
          rows — so they need this wrapper to position against, and it takes the
          fill classes the scroll container used to carry alone. */}
      <div className={cn("tw-relative tw-flex tw-flex-col", fill && FILL_HEIGHT_CLASSES)}>
        <div
          ref={scrollRef}
          onScroll={edges.onScroll}
          className={cn(
            "tw-overflow-auto",
            // A trackpad swipe past the last column must not become the
            // browser's back gesture, nor scroll the page behind the table.
            "tw-overscroll-x-contain",
            fill && FILL_HEIGHT_CLASSES
          )}
        >
          <table className={tableClassName} style={resize.tableStyle}>
            {/* The widths live on the `<col>`s rather than on the header cells:
                under `table-layout: fixed` the column elements are consulted
                first, and a width written once cannot disagree with the `<th>`
                that happens to carry it. */}
            {resize.seeded && (
              <colgroup>
                {effectiveColumns.map((col) => (
                  <col key={col.key} style={{ width: resize.colWidth(col) }} />
                ))}
              </colgroup>
            )}
            {effectiveColumns.length > 0 && (
              <thead>
                <tr>
                  {effectiveColumns.map((col, i) => {
                    const sortable = col.sortable && onSortChange;
                    const filterable = col.filter && onColumnFilterChange;
                    // The cell before a right-pinned one loses its tick: the
                    // frozen column's own left border already sits at that exact
                    // x, and two rules a pixel apart read as a smudge.
                    const beforePinned = effectiveColumns[i + 1]?.pinned === "right";
                    const resizer = resize.seeded ? resize.resizerProps(col) : null;
                    return (
                      <th
                        key={col.key}
                        ref={resize.setHeadRef(col.key)}
                        scope="col"
                        aria-sort={
                          sortable
                            ? sort?.key === col.key
                              ? sort.direction === "asc"
                                ? "ascending"
                                : "descending"
                              : "none"
                            : undefined
                        }
                        className={cn(
                          COLUMN_ALIGN_CLASSES[col.align],
                          col.pinned && PINNED_HEAD_CLASSES[col.pinned],
                          // Same shadow as the body cells, so the frozen edge
                          // runs the full height of the table rather than
                          // starting below the header.
                          col.pinned && edges[col.pinned] && PINNED_EDGE_CLASSES[col.pinned],
                          beforePinned && "after:!tw-hidden",
                          col.headClassName || col.className
                        )}
                        /* Once the `<colgroup>` is in, a width here is a second
                           answer to the same question — and the one the browser
                           ignores. */
                        style={col.width && !resize.seeded ? { width: col.width } : undefined}
                      >
                        {/* Sort and filter share the cell. The funnel is pushed to
                            the far end so the labels stay on one reading line down
                            the row instead of each starting after its neighbour's
                            controls — and `justify-between` collapses to nothing
                            when a column has neither. */}
                        <span
                          className={cn(
                            "tw-flex tw-items-center tw-gap-1.5",
                            filterable && "tw-justify-between",
                            col.align === "right" && "tw-flex-row-reverse",
                            col.align === "center" && "tw-justify-center"
                          )}
                        >
                          {sortable ? (
                            <SortableHeader column={col} sort={sort} onSortChange={onSortChange} />
                          ) : (
                            col.label
                          )}
                          {filterable && (
                            <ColumnFilter
                              column={col}
                              value={columnFilters[col.key]}
                              onChange={(next) => onColumnFilterChange(col.key, next)}
                            />
                          )}
                        </span>
                        {resizer && (
                          <span
                            {...resizer}
                            className={cn(
                              RESIZER_CLASSES,
                              // Held. The line stays lit while the pointer is
                              // anywhere on screen, which is where it goes the
                              // moment the drag starts.
                              resize.activeKey === col.key && "after:!tw-bg-primary"
                            )}
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
            )}
            <tbody>{body()}</tbody>
          </table>
        </div>

        {/* Only where nothing is frozen to that edge — otherwise the frozen
            cells are already carrying the shadow, and a fade painted over a
            frozen column would say it scrolls. `aria-hidden`, because "there is
            more to the right" is already the scroll container's own semantics. */}
        {!hasPinnedLeft && (
          <span
            aria-hidden="true"
            className={cn(
              EDGE_FADE_CLASSES,
              "tw-left-0 tw-bg-gradient-to-r tw-from-muted-fg/20",
              edges.left ? "tw-opacity-100" : "tw-opacity-0"
            )}
          />
        )}
        {!hasPinnedRight && (
          <span
            aria-hidden="true"
            className={cn(
              EDGE_FADE_CLASSES,
              "tw-right-0 tw-bg-gradient-to-l tw-from-muted-fg/20",
              edges.right ? "tw-opacity-100" : "tw-opacity-0"
            )}
          />
        )}
      </div>

      {pagination && (
        <div className="tw-shrink-0 tw-border-t tw-border-line-soft tw-px-4 tw-py-2 tw-bg-surface-raised">
          <Paginator showSinglePage {...pagination} />
        </div>
      )}
    </div>
  );
};

export default AppDataTable;
